import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ReportAuthor } from '../types/report'
import { Client } from 'store/src/lib/services/types';
import AuthorBadge from '@/components/raytd/user-avatar';
import { Address } from 'store/src/lib/asset';
import { Skeleton } from '@/components/ui/skeleton';

interface PreparedSectionProps {
  client?: Client;
  authors?: ReportAuthor[];
  editMode: boolean;
}

export function PreparedSection({ client, authors = [], editMode }: PreparedSectionProps) {
  const primaryAuthor = authors.find(author => author.isPrimary);
  const secondaryAuthors = authors
    .filter(author => !author.isPrimary)
    .sort((a, b) => a.name.localeCompare(b.name));


  if (!client) {
    return (
      <div className="h-20 flex flex-row w-full gap-4">
        <Skeleton className="h-16 w-16" />
        <Skeleton className="h-16 w-full" />
      </div>
    )
  }

  return (
    <section className="grid grid-cols-1 md:grid-cols-[300px_1fr] print:grid-cols-[220px_1fr] gap-6 print:gap-6 print:mt-4">

      {/* --- LEFT COLUMN: Prepared For --- */}
      <div className="min-w-0 overflow-hidden w-full space-y-4 max-w-[300px] print:max-w-none print:pr-4">
        <h2 className="text-lg font-medium mb-4">Prepared for</h2>
        <ClientPreparedForCard client={client} />
      </div>

      {/* --- RIGHT COLUMN: Prepared By --- */}
      <div className="min-w-0">
        <h2 className="text-lg font-medium mb-4">Prepared by</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 print:grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-6 print:gap-4">

          {primaryAuthor && (
            <div className="min-w-0 print:break-inside-avoid">
              <AuthorCard author={primaryAuthor} isPrimary={true} editMode={editMode} />
            </div>
          )}

          {secondaryAuthors.map(author => (
            <div key={author.id} className="min-w-0 print:break-inside-avoid">
              <AuthorCard author={author} isPrimary={false} editMode={editMode} />
            </div>
          ))}

        </div>
      </div>
    </section>
  )
}


interface AuthorCardProps {
  author: ReportAuthor;
  isPrimary: boolean;
  editMode: boolean;
}

function AuthorCard({ author, isPrimary, editMode }: AuthorCardProps) {
  return (
    <div className="min-w-0 w-full space-y-4 flex flex-col items-start">

      <AuthorBadge item={author} size='large' />

      <p className="font-medium break-words">{author.company?.title || ''}</p>

      <div className="flex min-w-0 w-full flex-col gap-0.5">
        <p className="font-semibold break-words">{`${author.name}`}</p>
        <p className="text-sm text-primary break-words">{author.position_title ?? ''}</p>
      </div>

      <AddressCard address={author.office?.address} />

      <div className="flex min-w-0 w-full flex-col gap-1">
        <p className="text-sm break-all">{author.email}</p>
        <p className="text-sm break-words">{author.phone}</p>
      </div>

    </div>
  )
}
interface ClientPreparedForCardProps {
  client: Client
}

function ClientPreparedForCard({ client }: ClientPreparedForCardProps) {
  return (
    <div className="min-w-0 w-full space-y-4 max-w-[300px] print:max-w-none">

      {client.logo?.thumbnail_url ? (
        <Avatar className="h-24 w-24">
          <AvatarImage
            src={client.logo.thumbnail_url}
            alt={`${client.organisation_name || 'Client'} logo`}
            className="object-cover"
          />
          <AvatarFallback>{client.organisation_name?.[0]}</AvatarFallback>
        </Avatar>
      ) : (
        <div className="h-24 w-24 invisible" aria-hidden="true" />
      )}

      <p className="font-medium break-words">{client?.organisation_name || ''}</p>

      <div className="flex min-w-0 w-full flex-col gap-0.5">
        <p className="font-semibold break-words">{`${client?.first_name || ''} ${client?.last_name || ''
          }`}</p>
        <p className="text-sm text-primary break-words">{client?.position || ''}</p>
      </div>

      <AddressCard address={client?.address} />

      <div className="flex min-w-0 w-full flex-col gap-1">
        <p className="text-sm break-all">{client?.email || ''}</p>
        <p className="text-sm break-words">{client?.phone || ''}</p>
      </div>
    </div>
  )
}


const AddressCard = ({ address }: { address: Partial<Address> }) => {
  return (
    <div className="flex min-w-0 w-full flex-col gap-0.5">
      {address && (
        <>
          <span className="text-sm break-words">{address.street}</span>
          <span className="text-sm break-words">{`${address.suburb}, ${address.state} ${address.postcode}`}</span>
        </>
      )}
    </div>
  )
}